import { useMemo } from "react";
import { Navigate } from "react-router-dom";

import { Box } from "@braintrust/braintrust-ui-components";
import { CantManageMessage } from "@js/apps/jobs/components";
import { useIdParam } from "@js/hooks/use-id-param";
import type { JobFormValues } from "@js/types/jobs";

import { useEditJob } from "../../apps/create-job/hooks/edit-job";
import { JobInterviewLoader } from "../../components/job-interview-loader";
import { InterviewQuestionsForm } from "../../forms/interview-questions";
import { useGenerateAiInterviewData } from "../../hooks/use-generate-ai-interview-data";
import { JobInterviewLayout } from "../../layout/job-interviews";

export const InterviewQuestionsPage = () => {
  const jobId = useIdParam();

  if (!jobId) {
    return <Navigate to="/page-not-found/?reloaded=true" replace />;
  }

  return (
    <JobInterviewLayout>
      <InterviewQuestionsPageContent />
    </JobInterviewLayout>
  );
};

const InterviewQuestionsPageContent = () => {
  const {
    job,
    loading,
    isFetchingJob,
    refetchJob,
    initialValues,
    ...formProps
  } = useEditJob();
  const { isGeneratingInterviewData, enableInterview } =
    useGenerateAiInterviewData({
      job,
      isJobLoading: isFetchingJob,
      refetchJob,
    });

  const initialInterviewFormValues = useMemo<Partial<JobFormValues>>(() => {
    const defaultAiInterviewMaxAttempts = enableInterview ? 3 : 1;
    const interviewInitialValues = {
      ...initialValues,
      ai_interview_maximum_attempts:
        job?.ai_interview_maximum_attempts ?? defaultAiInterviewMaxAttempts,
      ai_interview_max_duration_in_minutes:
        job?.ai_interview_max_duration_in_minutes ?? 15,
    };
    if (!enableInterview) {
      return interviewInitialValues;
    }

    const initialFormValues: Partial<JobFormValues> = {
      ...interviewInitialValues,
      is_ai_interview_enabled: true,
    };

    return initialFormValues;
  }, [initialValues, enableInterview, job]);

  const isLoading = isFetchingJob || isGeneratingInterviewData;
  const customContent = useMemo(() => {
    if (isGeneratingInterviewData) {
      return (
        <Box sx={{ flex: 1, display: "grid", placeItems: "center" }}>
          <JobInterviewLoader
            text="Creating your AI Interview"
            subtext="Generating your question suggestions..."
          />
        </Box>
      );
    }

    if (!job) {
      return (
        <Box sx={{ flex: 1, display: "grid", placeItems: "center" }}>
          <JobInterviewLoader />
        </Box>
      );
    }

    if (!job.user_can_manage) {
      return (
        <CantManageMessage
          title="You can't manage talent interviews in this job."
          to={`/jobs/${job?.id}`}
        />
      );
    }

    return null;
  }, [job, isGeneratingInterviewData]);

  return customContent ? (
    <InterviewQuestionsForm
      job={job}
      customContent={customContent}
      isLoading={isLoading}
    />
  ) : (
    <InterviewQuestionsForm
      job={job}
      isLoading={isLoading}
      {...formProps}
      initialValues={initialInterviewFormValues}
    />
  );
};
